'use client';
import React, {
  useCallback,
  useEffect,
  useContext,
  useRef,
  useState
} from 'react';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import Link from 'next/link';
import SvgDownCaret from '@/components/Icons/SvgDownCaret';
import { HeaderUIStateContext } from '@/components/header/header_menu/HeaderUIStateProvider';
import { getLinkTargetData } from '@/utils/getLinkTargetData';

function MenuItem({
  item,
  index,
  pathname,
  children,
  hasTouchScreen,
  navHoverItem,
  handleNavHover,
  openIndex,
  setOpenIndex,
  menuItemRefs,
  setScrollLock
}) {
  const [mounted, setMounted] = useState(false);

  const {
    has_subnav,
    label,
    label_link,
    label_query_string,
    label_text_color,
    external_link,
    _metadata,
    title
  } = item;

  useEffect(() => {
    setMounted(true);
  }, []);

  const linkData = getLinkTargetData({
    label,
    label_link,
    external_link,
    title
  });

  if (!linkData) return null;

  const { linkText, linkUrl, linkTitle } = linkData;
  const isActive = pathname === linkUrl;
  const isOpen = openIndex === index;

  const id = `desktop-navitem-${label.replaceAll(' ', '-').toLowerCase()}`;

  const closeMenu = () => {
    setOpenIndex(null);
    setScrollLock(false);
  };

  // Default to Link during SSR, then use actual hasTouchScreen value on client
  const useLink =
    !mounted || !hasTouchScreen || (hasTouchScreen && !has_subnav);
  const Tag = useLink ? Link : 'div';

  const handleKeyDown = (e) => {
    // Navigate left/right between menu items without auto-opening submenus
    if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
      if (menuItemRefs.current && menuItemRefs.current.length === 0) return;
      e.preventDefault();

      // [ROY] Added the following bandaid to fix an issue where arrowing
      // doesn't work correctly depending on the order in CS. But, in general
      // we've let the nav get a little too messy. I created a follow-up ticket
      // to clean up the nav logic and address all these index issues.

      // Focus the next non-empty menu item.
      // menuItemRefs is not necessarily continuous--it could look like
      // [ref, null, ref, ref, null, ref]
      const nonEmptyIndices = menuItemRefs.current.reduce((acc, ref, idx) => {
        if (ref) acc.push(idx);
        return acc;
      }, []);
      const currentIdxInNonEmpty = nonEmptyIndices.indexOf(index);
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      const nextIdxInNonEmpty =
        (currentIdxInNonEmpty + dir + nonEmptyIndices.length) %
        nonEmptyIndices.length;
      const nextIndex = nonEmptyIndices[nextIdxInNonEmpty];
      const nextItem = menuItemRefs.current[nextIndex];

      closeMenu();
      nextItem?.focus();
    }

    if (!has_subnav) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setOpenIndex(index);
        setScrollLock(true);
      } else {
        closeMenu();
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (!isOpen) {
        setOpenIndex(index);
        setScrollLock(true);
      }

      const subnav = document.getElementById(`subnav-${index}`);
      const firstItem = subnav?.querySelector(
        'a, button, [tabindex]:not([tabindex="-1"])'
      );
      firstItem?.focus();
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
      menuItemRefs.current[index]?.focus();
    }
  };

  return (
    <li
      id={id}
      key={_metadata.uid}
      className={`${has_subnav ? 'subnav' : ''} border-b-4 ${
        navHoverItem?.id === id ? 'border-cozy-cognac' : 'border-transparent'
      } group/mainnavitem relative`}
      data-cy={`desktop-navitem-${label.replaceAll(' ', '-').toLowerCase()}`}
      onMouseEnter={(e) => {
        handleNavHover(e);
        if (has_subnav) {
          setOpenIndex(index);
          setScrollLock(true);
        }
      }}
      onMouseLeave={() => {
        handleNavHover(undefined);
        if (has_subnav) {
          closeMenu();
        }
      }}
      aria-controls={has_subnav ? `subnav-${index}` : undefined}
    >
      <Tag
        className="body-r svg-stroke h-full"
        {...(useLink
          ? {
              href: `${linkUrl}${label_query_string ?? ''}`,
              prefetch: null,
              title: linkTitle,
              'data-heap': `mainNav-${index}${linkUrl.replaceAll('/', '-').toLowerCase()}`
            }
          : { tabIndex: 0 })}
        onKeyDown={handleKeyDown}
        ref={(el) => (menuItemRefs.current[index] = el)}
        aria-current={isActive ? 'page' : undefined}
        onClick={(e) => {
          // Prevent navigation on touch devices with subnavs
          if (hasTouchScreen && has_subnav) {
            e.preventDefault();
          }
        }}
        {...(has_subnav
          ? {
              'aria-haspopup': true,
              'aria-expanded': isOpen
            }
          : {})}
      >
        <span
          className={`${label_text_color} relative top-[2px] flex h-full items-center lg:px-6 ${isActive ? 'text-cozy-cognac' : label_text_color}`}
        >
          {linkText}
          {has_subnav && (
            <span
              className={`ml-1 inline-flex items-center transition-transform duration-300 ${isOpen ? 'scale-y-[-1]' : ''}`}
            >
              <SvgDownCaret />
            </span>
          )}
        </span>
      </Tag>

      {has_subnav && (
        <div
          id={`subnav-${index}`}
          className={`pointer-events-none fixed top-[60px] right-0 left-0 z-10 h-[calc(100dvh-110px)] overflow-x-hidden overflow-y-auto overscroll-contain ${isOpen ? 'block' : 'hidden'}`}
          aria-label={`${label} submenu`}
        >
          <div className="pointer-events-auto" onMouseLeave={closeMenu}>
            {React.Children.toArray(children)[index]}
          </div>
        </div>
      )}
    </li>
  );
}

export default function DesktopNav({ data, children }) {
  const pathname = useAppPathname();
  const {
    isDesktop,
    hasTouchScreen,
    navHoverItem,
    handleNavHover,
    setScrollLock
  } = useContext(HeaderUIStateContext);

  const menuItemRefs = useRef([]);
  const [openIndex, setOpenIndex] = useState(null);

  const closeOpenNav = useCallback(() => {
    setOpenIndex(null);
    setScrollLock(false);
  }, [setScrollLock]);

  useEffect(() => {
    handleNavHover(undefined);
    setOpenIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const menuItem = menuItemRefs.current[openIndex];
    const hasSubnav = menuItem?.closest('li')?.classList.contains('subnav');

    if (openIndex !== null && hasSubnav) {
      setScrollLock(true);
    } else {
      setScrollLock(false);
    }
  }, [openIndex, setScrollLock]);

  useEffect(() => {
    if (openIndex === null) return;

    const handleFocusOut = () => {
      requestAnimationFrame(() => {
        const subnav = document.getElementById(`subnav-${openIndex}`);
        const navItem = menuItemRefs.current[openIndex];

        const isInsideSubnav = subnav?.contains(document.activeElement);
        const isInsideNavItem = navItem?.contains(document.activeElement);

        if (!isInsideSubnav && !isInsideNavItem) {
          closeOpenNav();
        }
      });
    };

    document.addEventListener('focusout', handleFocusOut);
    return () => document.removeEventListener('focusout', handleFocusOut);
  }, [closeOpenNav, openIndex]);

  useEffect(() => {
    if (openIndex === null) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        const subnav = document.getElementById(`subnav-${openIndex}`);
        const navItem = menuItemRefs.current[openIndex];

        const isInsideSubnav = subnav?.contains(document.activeElement);
        const isInsideNavItem = navItem?.contains(document.activeElement);

        if (isInsideSubnav || isInsideNavItem) {
          closeOpenNav();
          navItem?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeOpenNav, openIndex]);

  return (
    <>
      <nav
        className="2lg:block relative z-20 order-none hidden h-[inherit] lg:order-2"
        aria-label="Desktop Navigation"
        role="navigation"
        data-cy="desktop-navigation"
      >
        <ul className="flex h-[inherit] list-none">
          {data.map((item, index) => {
            if (item.item_display?.includes('desktop')) {
              return (
                <MenuItem
                  key={`desktop-mainNav-navItem-${index}`}
                  item={item}
                  index={index}
                  pathname={pathname}
                  isDesktop={isDesktop}
                  hasTouchScreen={hasTouchScreen}
                  navHoverItem={navHoverItem}
                  handleNavHover={handleNavHover}
                  openIndex={openIndex}
                  setOpenIndex={setOpenIndex}
                  menuItemRefs={menuItemRefs}
                  setScrollLock={setScrollLock}
                >
                  {children}
                </MenuItem>
              );
            }
          })}
        </ul>
      </nav>

      {openIndex !== null && (
        <div
          className="bg-dawn-grey/60 fixed top-[60px] left-0 z-0 block h-screen w-screen"
          onMouseEnter={closeOpenNav}
        />
      )}
    </>
  );
}
